import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../cart/CartContext";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch, ApiError } from "../api/client";
import { paymentErrorKey } from "../api/paymentError";
import { useIsMobile } from "../hooks/useIsMobile";
import { isValidPhone } from "../lib/contactValidation";
import { formatEuro } from "../lib/format";
import { ga4 } from "../analytics/ga4";
import { pathFor } from "../app/paths";
import { analyticsIds } from "../analytics/tracker";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

/**
 * I codici VAT che parlano del numero inserito, e non del servizio che lo verifica.
 *
 * Il backend ne restituisce sette, e sono due famiglie diverse travestite da una:
 * questi quattro dicono «questo numero non va», gli altri tre (`vat_unavailable`,
 * `vat_pending_timeout`, `vat_verification_failed`) dicono «non sono riuscito a
 * chiederlo al registro». Solo la prima famiglia deve segnare il campo in rosso:
 * per la seconda il cliente non ha sbagliato niente e non c'è niente da correggere.
 */
const VAT_ERRORI_DEL_NUMERO = new Set([
  "vat_required",
  "vat_invalid",
  "vat_not_german",
  "vat_unverified",
]);

export function Checkout() {
  const { locale } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { items, totalCents, clearCart, removeItem, advancePayment, quote } = useCart();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [removedItems, setRemovedItems] = useState<string[]>([]);

  // Validate cart items on mount — remove any that no longer exist
  useEffect(() => {
    if (items.length === 0) return;
    fetch(`${API_BASE}/api/v1/shop/cart/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
      }),
    })
      .then((r) => r.json())
      .then((data: { invalid?: string[] }) => {
        if (data.invalid && data.invalid.length > 0) {
          const removed: string[] = [];
          for (const id of data.invalid) {
            const item = items.find((i) => i.id === id);
            if (item) removed.push(item.title);
            removeItem(id);
          }
          setRemovedItems(removed);
        }
      })
      .catch(() => { /* ignore validation errors */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [shipping, setShipping] = useState({
    // VAT come primo campo: solo aziende tedesche con P.IVA valida possono acquistare.
    vat_number: "",
    shipping_name: "",
    shipping_address: "",
    shipping_city: "",
    shipping_postal_code: "",
    // Paese bloccato sulla Germania (input read-only). Il backend forza comunque
    // "Germany" a prescindere dal valore inviato: non aggirabile dal browser.
    shipping_country: "Germany",
    shipping_phone: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});

  // Normalizzazione VAT lato client (solo per il feedback UX; la verifica reale
  // avviene lato backend via Stripe/VIES). Uppercase + rimozione separatori.
  const normalizeVat = (v: string) => v.replace(/[\s.\-_/]/g, "").toUpperCase();
  const DE_VAT_RE = /^DE\d{9}$/;

  // Formato italiano condiviso "1.234,56 €" (simbolo incluso → nessun &euro; a parte).
  const formatPrice = (cents: number) => formatEuro(cents);

  const updateField = (field: string, value: string) => {
    setShipping((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handlePlaceOrder = async () => {
    if (items.length === 0) return;

    const required = ["shipping_name", "shipping_address", "shipping_city", "shipping_postal_code", "shipping_country", "shipping_phone"];
    const errors: Record<string, boolean> = {};
    let vatMsg = "";
    // VAT (primo campo, obbligatorio): pre-check di formato per feedback immediato.
    // Il blocco REALE resta lato backend (verifica Stripe/VIES).
    const vat = normalizeVat(shipping.vat_number);
    if (!vat) {
      errors["vat_number"] = true;
      vatMsg = t("checkout.vat.errors.vat_required");
    } else if (!DE_VAT_RE.test(vat)) {
      errors["vat_number"] = true;
      vatMsg = t("checkout.vat.errors.vat_invalid");
    }
    for (const field of required) {
      const val = shipping[field as keyof typeof shipping].trim();
      if (!val) {
        errors[field] = true;
      } else if (val.length > 200) {
        errors[field] = true;
      }
    }
    // Postal code: 3-10 alphanumeric
    const pc = shipping.shipping_postal_code.trim();
    if (pc && !/^[a-zA-Z0-9\s-]{3,10}$/.test(pc)) {
      errors["shipping_postal_code"] = true;
    }
    // Phone: obbligatorio (già in `required`) e, se presente, deve essere valido.
    // Stessa validazione condivisa dei form contatti (lib/contactValidation).
    const phone = shipping.shipping_phone.trim();
    if (phone && !isValidPhone(phone)) {
      errors["shipping_phone"] = true;
    }
    // Name: no HTML/script tags
    const name = shipping.shipping_name.trim();
    if (name && /<[^>]+>/.test(name)) {
      errors["shipping_name"] = true;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Il messaggio VAT è il più preciso e ha la precedenza. Se però a mancare è
      // un altro campo, senza ripiego resterebbe la stringa vuota: il riquadro
      // dell'errore non viene reso affatto e premere «Conferma ordine» sembra non
      // fare niente. Su schermo stretto il campo in rosso può essere fuori dalla
      // parte visibile, quindi non resta nessun segnale che dica cosa manca.
      setError(vatMsg || t("checkout.errors.missingFields"));
      return;
    }

    // Evita doppi invii mentre la verifica VAT/ordine è in corso.
    if (submitting) return;
    setSubmitting(true);
    setError("");

    try {
      // 1. Crea l'ordine (stato PENDING, o PAID se sconto porta il totale a €0).
      // Gli identificatori analytics viaggiano QUI: con il pagamento online in
      // pausa non esiste più una seconda chiamata (la sessione Stripe) in cui
      // ordine e sessione di navigazione si incontrino. Senza, ogni ordine
      // resterebbe per sempre senza provenienza.
      const order = await apiFetch<{ id: string; status: string; total_cents: number }>("/api/v1/shop/orders", {
        method: "POST",
        body: JSON.stringify({
          items: items.map((item) => ({
            product_id: item.id,
            quantity: item.quantity,
          })),
          advance_payment: advancePayment,
          discount_code: discountCode.trim() || null,
          ...shipping,
          ...analyticsIds(),
        }),
      });
      // GA4 begin_checkout: emesso solo quando l'ordine esiste davvero lato server
      // (valore = total_cents del server, lordo IVA). Va chiamato PRIMA di
      // clearCart(), che azzera gli item. Se la creazione fallisce si finisce nel
      // catch e l'evento non parte: nessun begin_checkout fantasma.
      const trackBeginCheckout = () => ga4.beginCheckout(
        items.map((i) => ({ item_id: i.slug || i.id, item_name: i.title, price: i.price_cents / 100, quantity: i.quantity })),
        order.total_cents,
        items[0]?.currency || "EUR",
      );

      // 1b. Ordine gratuito (es. sconto 100% → €0): niente Stripe, già PAID → success.
      if (order.total_cents <= 0 || order.status === "PAID") {
        trackBeginCheckout();
        clearCart();
        navigate(`/${locale}/checkout/success?order_id=${order.id}`);
        return;
      }
      // ── 2. PAGAMENTO GESTITO DALLE PERSONE (Stripe in pausa) ───────────────
      // L'ordine è registrato e il backend ha già inviato le due email: la
      // richiesta al team e la conferma al cliente. Il percorso si ferma qui e
      // si va alla pagina di richiesta ricevuta; l'incasso lo concorda un
      // consulente fuori dal sito.
      trackBeginCheckout();
      clearCart();
      navigate(`/${locale}/checkout/success?order_id=${order.id}&mode=request`);

      /* ── PER RIACCENDERE STRIPE ─────────────────────────────────────────────
         1. Cancellare le tre righe qui sopra (da `trackBeginCheckout()` a
            `navigate(...)`), che sono la deviazione verso la pagina richiesta.
         2. Decommentare i passi 2 e 3 qui sotto.
         3. Rimettere MANUAL_PAYMENT_MODE=false nel .env del backend: se resta
            true, il cliente riceve la conferma di RICHIESTA al posto di quella
            di PAGAMENTO (i due invii condividono lo stesso claim di idempotenza).
         Niente altro da toccare: la pagina di successo torna da sola al
         comportamento con polling quando l'URL non ha più `mode=request`.

      // 2. Crea la Stripe Checkout Session per quell'ordine (importo dal server).
      const session = await apiFetch<{ checkout_url: string }>(
        "/api/v1/payments/stripe/checkout-session",
        // Gli identificatori analytics viaggiano qui perché questo è l'unico
        // momento in cui l'ordine e la sessione di navigazione esistono nello
        // stesso contesto: il webhook Stripe arriva dopo, dai server di Stripe,
        // e non può sapere da quale scheda del browser fosse partito l'acquisto.
        { method: "POST", body: JSON.stringify({ order_id: order.id, ...analyticsIds() }) },
      );
      // 3. Sessione creata: traccia, svuota il carrello e reindirizza a Stripe.
      trackBeginCheckout();
      clearCart();
      window.location.assign(session.checkout_url);
      */
    } catch (err: unknown) {
      // Errore VAT (422 vat_*): messaggio professionale dedicato, mai il codice grezzo.
      if (err instanceof ApiError && typeof err.message === "string" && err.message.startsWith("vat_")) {
        const key = `checkout.vat.errors.${err.message}`;
        const msg = t(key);
        setError(msg === key ? t("checkout.vat.errors.generic") : msg);
        // Il bordo rosso accusa il campo, quindi si accende solo quando è davvero
        // il numero a non andare. Se il registro europeo non ha saputo rispondere,
        // il dato del cliente è a posto e segnarlo come sbagliato lo manda a
        // ricontrollare una partita IVA corretta — che è esattamente quello che
        // non deve fare: deve solo riprovare.
        if (VAT_ERRORI_DEL_NUMERO.has(err.message)) {
          setFormErrors((prev) => ({ ...prev, vat_number: true }));
        }
      }
      // Codice sconto non valido (422 discount_*): messaggio dedicato.
      else if (err instanceof ApiError && typeof err.message === "string" && err.message.startsWith("discount_")) {
        setError(t("checkout.discountInvalid"));
      } else {
        // Messaggio localizzato per status code; mai la stringa tecnica del backend.
        setError(t(paymentErrorKey(err)));
      }
      setSubmitting(false);
    }
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    width: "100%",
    border: formErrors[field] ? "1px solid #ef4444" : "1px solid rgba(28,75,59,0.89)",
    borderRadius: 8,
    padding: "11px 14px",
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    background: cfg.color_light_bg,
    transition: "border-color 0.2s",
  });

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 4,
    color: cfg.color_dark,
    letterSpacing: 0.2,
  };

  const overline: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: cfg.color_muted,
    marginBottom: 16,
  };

  return (
    <div style={{ padding: isMobile ? "32px 20px 64px" : "64px 24px 96px", maxWidth: 960, margin: "0 auto" }}>
      {removedItems.length > 0 && (
        <div style={{
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 24,
          fontSize: 13,
          color: "#92400e",
          lineHeight: 1.5,
        }}>
          {t("checkout.removedItems")} <strong>{removedItems.join(", ")}</strong>
        </div>
      )}
      <div style={overline}>{t("checkout.title")}</div>
      <h1 style={{
        fontFamily: cfg.font_heading,
        fontSize: isMobile ? 28 : 36,
        fontWeight: 400,
        color: cfg.color_dark,
        margin: "0 0 48px 0",
        lineHeight: 1.2,
      }}>
        {t("checkout.title")}
      </h1>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 360px",
        gap: isMobile ? 32 : 48,
        alignItems: "start",
      }}>
        {/* Shipping form */}
        <div style={{
          background: cfg.color_light_bg,
          borderRadius: 8,
          padding: isMobile ? "24px 20px" : "32px",
        }}>
          <div style={{ ...overline, marginBottom: 24 }}>{t("checkout.shippingTitle")}</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* VAT — primo campo, obbligatorio. Solo aziende tedesche con P.IVA valida. */}
            <div>
              <label style={labelStyle}>{t("checkout.vat.label")} *</label>
              <input
                type="text"
                value={shipping.vat_number}
                onChange={(e) => updateField("vat_number", e.target.value)}
                onBlur={(e) => updateField("vat_number", normalizeVat(e.target.value))}
                style={inputStyle("vat_number")}
                placeholder={t("checkout.vat.placeholder")}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
              />
              <p style={{ fontSize: 11, color: formErrors.vat_number ? "#ef4444" : cfg.color_muted, marginTop: 4, lineHeight: 1.4 }}>
                {t("checkout.vat.help")}
              </p>
            </div>
            <div>
              <label style={labelStyle}>{t("checkout.shippingName")} *</label>
              <input
                type="text"
                value={shipping.shipping_name}
                onChange={(e) => updateField("shipping_name", e.target.value)}
                style={inputStyle("shipping_name")}
                placeholder={t("checkout.shippingNamePh")}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("checkout.shippingAddress")} *</label>
              <input
                type="text"
                value={shipping.shipping_address}
                onChange={(e) => updateField("shipping_address", e.target.value)}
                style={inputStyle("shipping_address")}
                placeholder={t("checkout.shippingAddressPh")}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>{t("checkout.shippingCity")} *</label>
                <input
                  type="text"
                  value={shipping.shipping_city}
                  onChange={(e) => updateField("shipping_city", e.target.value)}
                  style={inputStyle("shipping_city")}
                />
              </div>
              <div>
                <label style={labelStyle}>{t("checkout.shippingPostalCode")} *</label>
                <input
                  type="text"
                  value={shipping.shipping_postal_code}
                  onChange={(e) => updateField("shipping_postal_code", e.target.value)}
                  style={inputStyle("shipping_postal_code")}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>{t("checkout.shippingCountry")} *</label>
              {/* Paese bloccato: solo aziende tedesche. Read-only; il backend forza
                  comunque "Germany" a prescindere dal valore inviato. */}
              <input
                type="text"
                value={t("checkout.germany")}
                readOnly
                aria-readonly="true"
                tabIndex={-1}
                style={{ ...inputStyle("shipping_country"), cursor: "not-allowed", opacity: 0.75 }}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("checkout.shippingPhone")} *</label>
              <input
                type="tel"
                value={shipping.shipping_phone}
                onChange={(e) => updateField("shipping_phone", e.target.value)}
                style={inputStyle("shipping_phone")}
              />
            </div>
            <div>
              <label style={labelStyle}>{t("checkout.notes")}</label>
              <textarea
                value={shipping.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                rows={3}
                style={{ ...inputStyle("notes"), resize: "vertical" }}
              />
            </div>
          </div>
        </div>

        {/* Order summary */}
        <div style={{
          background: cfg.color_dark,
          borderRadius: 8,
          padding: "28px 24px",
          color: "#fff",
          position: isMobile ? "static" : "sticky",
          top: 96,
        }}>
          <div style={{
            fontSize: 11,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: 2,
            color: cfg.color_muted,
            marginBottom: 20,
          }}>
            {t("checkout.orderSummary")}
          </div>

          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px 0" }}>
            {items.map((item) => (
              <li key={item.id} style={{
                fontSize: 13,
                padding: "8px 0",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
                <span>
                  {item.title} <span style={{ opacity: 0.5 }}>x{item.quantity}</span>
                </span>
                <span style={{ fontWeight: 500 }}>
                  {formatPrice(item.price_cents * item.quantity)}
                </span>
              </li>
            ))}
          </ul>

          <div style={{
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,0.15)",
            marginBottom: 24,
            display: "flex",
            flexDirection: "column",
            gap: 7,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.8 }}>
              <span>{t("cart.subtotal")}</span>
              <span>{formatPrice(quote ? quote.subtotal_cents : totalCents)}</span>
            </div>
            {quote && quote.total_discount_cents > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#9fe0b4", fontWeight: 500 }}>
                <span>{t("cart.discount")}</span>
                <span>&minus;{formatPrice(quote.total_discount_cents)}</span>
              </div>
            )}
            {quote && quote.vat_rate_percent > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.8 }}>
                  <span>{t("cart.taxableBase")}</span>
                  <span>{formatPrice(quote.taxable_cents)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, opacity: 0.8 }}>
                  <span>{t("cart.vat", { rate: quote.vat_rate_percent })}</span>
                  <span>{formatPrice(quote.vat_cents)}</span>
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 600, paddingTop: 6 }}>
              <span>{quote && quote.vat_rate_percent > 0 ? t("cart.totalInclVat") : t("checkout.total")}</span>
              <span>{formatPrice(quote ? (quote.vat_rate_percent > 0 ? quote.total_gross_cents : quote.total_cents) : (cfg.vat_rate_percent > 0 ? totalCents + Math.round((totalCents * cfg.vat_rate_percent) / 100) : totalCents))}</span>
            </div>
            <p style={{ fontSize: 10.5, opacity: 0.55, margin: 0, textAlign: "right" }}>
              {t("cart.shippingVatNote")}
            </p>
          </div>

          {/* Codice sconto */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: cfg.color_muted, marginBottom: 6 }}>
              {t("checkout.discountCode")}
            </label>
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder={t("checkout.discountCodePh")}
              autoCapitalize="characters"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.06)",
                color: "#fff",
                fontSize: 13,
                outline: "none",
              }}
            />
          </div>

          {error && (
            <p style={{ fontSize: 12, color: "#fca5a5", marginBottom: 12 }}>{error}</p>
          )}

          {submitting && (
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
              {t("checkout.vat.verifying")}
            </p>
          )}

          <button
            onClick={handlePlaceOrder}
            disabled={submitting || items.length === 0}
            style={{
              width: "100%",
              background: items.length === 0 ? "rgba(255,255,255,0.1)" : cfg.color_primary,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 500,
              cursor: items.length === 0 ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
              transition: "opacity 0.2s",
              letterSpacing: 0.3,
            }}
          >
            {submitting ? t("checkout.processing") : t("checkout.placeOrder")}
          </button>
        </div>
      </div>

      <Link
        to={pathFor("collections", locale)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          marginTop: 40,
          color: cfg.color_muted,
          fontSize: 13,
          textDecoration: "none",
          fontWeight: 500,
          letterSpacing: 0.3,
          transition: "color 0.2s",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
        {t("checkout.backToProducts")}
      </Link>
    </div>
  );
}
